import React, { useEffect, useState } from 'react';
import { useLang } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { adminListContacts, adminUpdateContact, adminDeleteContact } from '../../api';
import { toast } from '../../hooks/use-toast';
import { Mail, Phone, Trash2, CheckCircle2, MessageSquare, Send } from 'lucide-react';

const AdminContacts = () => {
  const { lang } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminListContacts();
      setItems(data || []);
    } catch { toast({ title: 'Load failed', variant: 'destructive' }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markHandled = async (id) => {
    try {
      await adminUpdateContact(id, { status: 'handled' });
      toast({ title: lang === 'ar' ? 'تم التحديث' : 'Marked as handled' });
      load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const saveReply = async () => {
    if (!selected || !reply.trim()) return;
    try {
      await adminUpdateContact(selected.id, { admin_reply: reply, status: 'replied' });
      toast({ title: lang === 'ar' ? 'تم حفظ الرد' : 'Reply saved' });
      setSelected(null); setReply(''); load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const remove = async (id) => {
    if (!window.confirm(lang === 'ar' ? 'حذف؟' : 'Delete?')) return;
    try { await adminDeleteContact(id); load(); }
    catch { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const statusColors = {
    new: 'bg-blue-500/15 text-blue-400 border-blue-400/30',
    handled: 'bg-amber-500/15 text-amber-400 border-amber-400/30',
    replied: 'bg-blue-500/15 text-blue-400 border-blue-400/30'
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl text-[#0a0a0a]">{lang === 'ar' ? 'رسائل التواصل' : 'Contact Messages'}</h1>
        <p className="text-sm text-[#111827]/60 mt-2">{items.length} {lang === 'ar' ? 'رسالة' : 'messages'}</p>
      </div>

      {loading ? <div className="glass-card rounded-2xl p-10 text-center text-[#111827]/60">…</div>
      : items.length === 0 ? <div className="glass-card rounded-2xl p-10 text-center text-[#111827]/60">{lang === 'ar' ? 'لا توجد رسائل' : 'No messages'}</div>
      : (
        <div className="space-y-3">
          {items.map((c) => (
            <div key={c.id} className="glass-card rounded-2xl p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="font-display text-lg text-[#0a0a0a]">{c.name}</div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${statusColors[c.status] || statusColors.new}`}>{c.status}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-[#111827]/60 mb-3">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</span>
                    {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone}</span>}
                    <span>{(c.created_at || '').toString().split('T')[0]}</span>
                  </div>
                  {c.subject && <div className="text-xs uppercase tracking-widest text-[#2563eb] mb-1">{c.subject}</div>}
                  <div className="text-sm text-[#111827]/85 leading-relaxed whitespace-pre-wrap">{c.message}</div>
                  {c.admin_reply && (
                    <div className="mt-3 pt-3 border-t border-[#2563eb]/10">
                      <div className="text-[10px] uppercase tracking-widest text-blue-400 mb-1">{lang === 'ar' ? 'الرد' : 'Admin Reply'}</div>
                      <div className="text-sm text-[#111827]/85 whitespace-pre-wrap">{c.admin_reply}</div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <Button onClick={() => { setSelected(c); setReply(c.admin_reply || ''); }} className="gold-button rounded-md h-9 px-3 text-xs"><Send className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'رد' : 'Reply'}</Button>
                  {c.status !== 'handled' && c.status !== 'replied' && (
                    <Button onClick={() => markHandled(c.id)} className="ghost-button rounded-md h-9 px-3 text-xs bg-transparent"><CheckCircle2 className="w-3.5 h-3.5 me-1" /> {lang === 'ar' ? 'تمت' : 'Handle'}</Button>
                  )}
                  <Button onClick={() => remove(c.id)} className="ghost-button rounded-md h-9 px-3 text-xs bg-transparent border-rose-400/30 text-rose-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#ffffff] border-[#2563eb]/25 text-[#111827] max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-[#0a0a0a] font-display text-xl">{lang === 'ar' ? 'رد على' : 'Reply to'} {selected?.name}</DialogTitle>
            <div className="text-xs text-[#111827]/60">{selected?.email}</div>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-[#f8fafc] rounded-md p-3 text-sm text-[#111827]/80 max-h-32 overflow-y-auto">{selected?.message}</div>
            <Textarea rows={6} value={reply} onChange={(e) => setReply(e.target.value)} placeholder={lang === 'ar' ? 'اكتب ردك هنا...' : 'Type your reply here...'} className="bg-[#f8fafc] border-[#2563eb]/25 text-[#111827]" />
            <p className="text-[10px] text-[#111827]/40">{lang === 'ar' ? 'يتم حفظ الرد داخلياً. يمكنك نسخه وإرساله يدوياً عبر بريدك الإلكتروني.' : 'Reply is stored internally. Copy & send it via your email manually.'}</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setSelected(null)} className="ghost-button rounded-md h-10 px-4 text-xs bg-transparent">{lang === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
            <Button onClick={saveReply} className="gold-button rounded-md h-10 px-5 text-xs">{lang === 'ar' ? 'حفظ الرد' : 'Save Reply'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminContacts;
